import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'
import {
  Search,
  Filter,
  ArrowLeft,
  Package,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  MapPin,
  DollarSign,
  Truck,
  Phone,
  Palette,
  Ruler
} from 'lucide-react'

interface Order {
  id: string
  buyer_id: string
  seller_id: string
  product_id: string
  final_price: number
  status: 'pending' | 'confirmed' | 'paid' | 'delivered' | 'cancelled'
  delivery_type: 'standard' | 'express' | 'pickup'
  delivery_address: string
  confirmation_code: string | null
  created_at: string
  buyer: {
    full_name: string | null
    email: string | null
    phone_number: string | null
  } | null
  seller: {
    full_name: string | null
    email: string | null
  } | null
  product: {
    title: string
    price: number
    images: string[]
    selectedColor?: string
    selectedSize?: string
  }
}

interface DeliveryRequest {
  id: string
  order_id: string
  seller_id: string
  delivery_partner_id: string | null
  status: 'pending' | 'accepted' | 'completed' | 'cancelled'
  pickup_instructions: string | null
  delivery_notes: string | null
  created_at: string
}

export function AdminOrdersPage() {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'paid' | 'delivered' | 'cancelled'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  useEffect(() => {
    fetchOrders()
  }, [filter, searchQuery])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('orders')
        .select(`
          *,
          buyer:profiles!buyer_id (
            full_name,
            email,
            phone_number
          ),
          seller:profiles!seller_id (
            full_name,
            email
          ),
          product:products!product_id (
            title,
            price,
            images
          )
        `)
        .order('created_at', { ascending: false })

      if (filter !== 'all') {
        query = query.eq('status', filter)
      }

      if (searchQuery) {
        query = query.or(
          `buyer.full_name.ilike.%${searchQuery}%,buyer.email.ilike.%${searchQuery}%,product.title.ilike.%${searchQuery}%`
        )
      }

      const { data, error } = await query

      if (error) throw error
      setOrders(data || [])
    } catch (err) {
      console.error('Error fetching orders:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (orderId: string, newStatus: Order['status']) => {
    try {
      setLoading(true)
      
      // If admin approves the order, set it to paid status directly
      const finalStatus = newStatus === 'confirmed' ? 'paid' : newStatus

      // Update order status
      const { error: updateError } = await supabase
        .from('orders')
        .update({ 
          status: finalStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId)

      if (updateError) throw updateError

      // Get the order details
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          product:products (
            title
          ),
          seller:profiles!seller_id (
            id,
            full_name
          ),
          buyer:profiles!buyer_id (
            id,
            full_name
          )
        `)
        .eq('id', orderId)
        .single()

      if (orderError) throw orderError

      // Create notification for seller
      const { error: sellerNotifError } = await supabase
        .from('notifications')
        .insert([{
          user_id: orderData.seller?.id,
          title: 'Mise à jour de commande',
          content: `La commande #${orderId.slice(0, 8)} est maintenant ${
            finalStatus === 'paid' ? 'confirmée et payée' :
            finalStatus === 'delivered' ? 'livrée' : 'annulée'
          }`,
          type: 'order',
          metadata: {
            order_id: orderId,
            status: finalStatus,
            product_title: orderData.product?.title,
            amount: orderData.final_price,
            delivery_type: orderData.delivery_type,
            delivery_address: orderData.delivery_address
          }
        }])

      if (sellerNotifError) throw sellerNotifError

      // Create notification for buyer
      const { error: buyerNotifError } = await supabase
        .from('notifications')
        .insert([{
          user_id: orderData.buyer?.id,
          title: 'Mise à jour de commande',
          content: `Votre commande est maintenant ${
            finalStatus === 'paid' ? 'confirmée et payée' :
            finalStatus === 'delivered' ? 'livrée' : 'annulée'
          }`,
          type: 'order',
          metadata: {
            order_id: orderId,
            status: finalStatus,
            product_title: orderData.product?.title
          }
        }])

      if (buyerNotifError) throw buyerNotifError

      setToastMessage('Statut de la commande mis à jour avec succès')
      setToastType('success')
      setShowToast(true)
      
      // Refresh orders list
      fetchOrders()
    } catch (err) {
      console.error('Error updating order status:', err)
      setToastMessage('Erreur lors de la mise à jour du statut')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleRequestDelivery = async (order: Order) => {
    try {
      // Create delivery request
      const { error: requestError } = await supabase
        .from('delivery_requests')
        .insert([{
          order_id: order.id,
          seller_id: order.seller_id,
          pickup_instructions: `Récupérer le produit "${order.product.title}" chez le vendeur`,
          delivery_notes: `Livrer à l'adresse: ${order.delivery_address}`
        }])

      if (requestError) throw requestError

      // Create notification for delivery partners
      await supabase
        .from('notifications')
        .insert([{
          title: 'Nouvelle mission de livraison disponible',
          content: `Une nouvelle mission de livraison est disponible dans votre zone`,
          type: 'system',
          metadata: {
            order_id: order.id,
            delivery_type: order.delivery_type
          }
        }])
        .eq('role', 'delivery')
        .eq('status', 'active')

      setToastMessage('Demande de livraison créée avec succès')
      setToastType('success')
      setShowToast(true)
      setShowDetails(false)
    } catch (err) {
      console.error('Error creating delivery request:', err)
      setToastMessage('Erreur lors de la création de la demande de livraison')
      setToastType('error')
      setShowToast(true)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'confirmed': return 'bg-blue-100 text-blue-800'
      case 'paid': return 'bg-green-100 text-green-800'
      case 'delivered': return 'bg-purple-100 text-purple-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />
      case 'confirmed': return <CheckCircle className="h-4 w-4" />
      case 'paid': return <DollarSign className="h-4 w-4" />
      case 'delivered': return <Truck className="h-4 w-4" />
      case 'cancelled': return <XCircle className="h-4 w-4" />
      default: return <AlertCircle className="h-4 w-4" />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Order Details Modal */}
      {showDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails de la Commande
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Order Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations de la Commande</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-900">ID: {selectedOrder.id}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedOrder.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`}>
                        {getStatusIcon(selectedOrder.status)}
                        <span className="ml-1 capitalize">
                          {selectedOrder.status === 'pending' ? 'En attente' :
                           selectedOrder.status === 'confirmed' ? 'Confirmée' :
                           selectedOrder.status === 'paid' ? 'Payée' :
                           selectedOrder.status === 'delivered' ? 'Livrée' :
                           'Annulée'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Produit</h4>
                  <div className="mt-2 flex items-center">
                    <img
                      src={selectedOrder.product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                      alt={selectedOrder.product.title}
                      className="h-16 w-16 object-cover rounded"
                    />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        {selectedOrder.product.title}
                      </p>
                      <p className="text-sm text-gray-900">
                        Prix final: ${selectedOrder.final_price.toFixed(2)}
                      </p>
                      
                      {/* Product Options */}
                      <div className="mt-2 space-y-1">
                        {selectedOrder.selected_color && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Palette className="h-4 w-4 mr-1 text-gray-400" />
                            <span>Couleur: {selectedOrder.selected_color}</span>
                          </div>
                        )}
                        
                        {selectedOrder.selected_size && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Ruler className="h-4 w-4 mr-1 text-gray-400" />
                            <span>Taille: {selectedOrder.selected_size}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buyer Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Acheteur</h4>
                  <div className="mt-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {selectedOrder.buyer?.full_name || 'Client'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedOrder.buyer?.email}
                        </p>
                      </div>
                    </div>
                    {selectedOrder.buyer?.phone_number && (
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <Phone className="h-4 w-4 mr-2" />
                        {selectedOrder.buyer.phone_number}
                      </div>
                    )}
                  </div>
                </div>

                {/* Seller Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Vendeur</h4>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedOrder.seller?.full_name || 'Vendeur'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedOrder.seller?.email}
                    </p>
                  </div>
                </div>

                {/* Delivery Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Livraison</h4>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center text-sm text-gray-900">
                      <Truck className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="capitalize">{selectedOrder.delivery_type}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                      {selectedOrder.delivery_address}
                    </div>
                    {selectedOrder.confirmation_code && (
                      <div className="flex items-center text-sm text-gray-500">
                        <CheckCircle className="h-4 w-4 text-gray-400 mr-2" />
                        Code de confirmation: {selectedOrder.confirmation_code}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-6 border-t flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDetails(false)}
                  >
                    Fermer
                  </Button>
                  {selectedOrder.status === 'pending' && (
                    <>
                      <Button
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          handleStatusUpdate(selectedOrder.id, 'cancelled')
                          setShowDetails(false)
                        }}
                      >
                        Annuler
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => {
                          handleStatusUpdate(selectedOrder.id, 'confirmed')
                          setShowDetails(false)
                        }}
                      >
                        Approuver
                      </Button>
                    </>
                  )}
                  {selectedOrder.status === 'paid' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleRequestDelivery(selectedOrder)}
                      >
                        <Truck className="h-5 w-5 mr-2" />
                        Demander un livreur
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => {
                          handleStatusUpdate(selectedOrder.id, 'delivered')
                          setShowDetails(false)
                        }}
                      >
                        Marquer comme livré
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion des Commandes
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez et suivez toutes les commandes de la plateforme
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher une commande..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as typeof filter)}
                  className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="confirmed">Confirmées</option>
                  <option value="paid">Payées</option>
                  <option value="delivered">Livrées</option>
                  <option value="cancelled">Annulées</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucune commande ne correspond à vos critères
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commande
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendeur
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Livraison
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <img
                                src={order.product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                                alt={order.product.title}
                                className="h-10 w-10 rounded object-cover"
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {order.product.title}
                              </div>
                              <div className="text-sm text-gray-500">
                                #{order.id.slice(0, 8)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="h-4 w-4 text-gray-500" />
                              </div>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {order.buyer?.full_name || 'Client'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {order.buyer?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {order.seller?.full_name || 'Vendeur'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.seller?.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Truck className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="capitalize">{order.delivery_type}</span>
                          </div>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="truncate max-w-xs">
                              {order.delivery_address}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            ${order.final_price.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(order.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            <span className="ml-1 capitalize">
                              {order.status === 'pending' ? 'En attente' :
                               order.status === 'confirmed' ? 'Confirmée' :
                               order.status === 'paid' ? 'Payée' :
                               order.status === 'delivered' ? 'Livrée' :
                               'Annulée'}
                            </span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order)
                              setShowDetails(true)
                            }}
                          >
                            Détails
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}